import { ZONE_CONFIGS } from '../../../../helpers/general.helpers';
import { FOCUS_CONFIGS, getSportLayerColor, getSportTypeColor } from '../../training-log.helpers';

export interface TrainingZoneRow {
  zone: string;
  color: string;
  name: string;
  effort: string;
  description: string;
}

export interface LabeledText {
  label: string;
  text: string;
}

export interface SportSubType {
  name: string;
  color: string;
  description: string;
}

export interface SportSection {
  sport: string;
  sportColor: string;
  intro: string;
  subTypes: SportSubType[];
}

export interface FocusCategory {
  name: string;
  color: string;
  description: string;
}

export const TRAINING_ZONE_ROWS: TrainingZoneRow[] = ZONE_CONFIGS.map((config, index) => {
  const rows: Omit<TrainingZoneRow, 'zone' | 'color'>[] = [
    {
      name: 'Recovery / Active Rest',
      effort: 'Very easy',
      description:
        'Time spent below any meaningful aerobic stimulus — standing around, chatting, transitions between efforts, etc. Not beneficial to cardiovascular development. Important to track because it reveals how much of total "training time" is actually non-productive standing time rather than real work.',
    },
    {
      name: 'Low Aerobic',
      effort: 'Easy / conversational',
      description:
        'Foundation endurance. Builds aerobic base and fat oxidation capacity. Most training volume should be in this zone — the backbone of polarized training.',
    },
    {
      name: 'Moderate Aerobic',
      effort: 'Moderate / steady',
      description:
        'Upper aerobic range near the first lactate threshold. Tempo-style efforts that improve aerobic efficiency. Used in longer intervals and steady-state sessions.',
    },
    {
      name: 'Threshold',
      effort: 'Hard / uncomfortable',
      description:
        'At or near lactate threshold. Sustained efforts of 30 minutes to an hour. Develops the ability to hold high intensities for extended durations — key for race performance.',
    },
    {
      name: 'VO2 Max',
      effort: 'Very hard',
      description:
        'Hard sustained efforts of 15–30 minutes targeting maximum oxygen uptake. Drives VO2 max capacity — the primary season goal.',
    },
    {
      name: 'Anaerobic / Sprint',
      effort: 'Maximum / all-out',
      description:
        'Sprint efforts up to 3 minutes above VO2 max. Race finishes and anaerobic capacity work. Does not include maximal-output speed work under 30 seconds, as those efforts are too short to be registered by heart rate.',
    },
  ];

  return {
    zone: config.label,
    color: config.barColor,
    ...rows[index],
  };
});

export const PERIODIZATION_ITEMS: LabeledText[] = [
  {
    label: 'Period (Mesocycle):',
    text: 'The season is divided into 13 periods, each typically 4 weeks long. Each period has a specific training emphasis that shifts across the year — from base building early on, through intensity phases, to race-specific preparation and competition.',
  },
  {
    label: 'Weekly Volume:',
    text: 'Total training time per week, measured in hours. This is the primary metric for tracking training load. Volume generally builds through the season, with planned recovery weeks (lower volume) within each period.',
  },
  {
    label: 'Duration vs. Distance:',
    text: "Most charts offer a toggle between these two views. Duration (hours) reflects time commitment and training load. Distance (km) reflects output and is useful for comparing across sport types. Some activities (like gym work) don't have meaningful distance values.",
  },
  {
    label: 'Ascent (m):',
    text: 'Total meters of elevation gain. Especially relevant for roller-ski and ski sessions where vertical plays a large role in training stress. A high-ascent session at low heart rate can be more demanding than a flat session at higher heart rate.',
  },
  {
    label: 'Avg Weekly Hours:',
    text: 'The season-wide average of weekly training volume. Useful as a benchmark — individual weeks above this number are higher-load weeks, and those below are recovery or taper weeks.',
  },
];

interface SportSectionContent {
  sport: string;
  intro: string;
  subTypes: { name: string; description: string }[];
}

const SPORT_SECTIONS_CONTENT: SportSectionContent[] = [
  {
    sport: 'XC Skiing',
    intro: 'The primary sport. The split between skate and classic technique is a key training variable.',
    subTypes: [
      {
        name: 'Rollerski Skate',
        description: 'Skate technique on pavement using roller skis. Primary dryland ski-specific training.',
      },
      {
        name: 'Rollerski Classic',
        description: 'Classic technique on pavement using roller skis. Builds diagonal stride and double pole.',
      },
      {
        name: 'Skate Ski',
        description: 'On-snow skate skiing. Used during the winter competition season.',
      },
      {
        name: 'Classic Ski',
        description: 'On-snow classic skiing. Used during the winter competition season.',
      },
      {
        name: 'Specific Foot',
        description:
          'Ski-specific running, bounding, and hill striding that mimics skiing movement patterns without skis.',
      },
      {
        name: 'Specific Strength',
        description:
          'Ski-specific strength work using bands, poles, or bodyweight to target skiing muscle groups and movement patterns.',
      },
    ],
  },
  {
    sport: 'Run',
    intro:
      'Road running used for aerobic base building and cross-training. Supports cardiovascular fitness without the equipment demands of skiing.',
    subTypes: [
      {
        name: 'Road',
        description:
          'General road running. Most running is done in Z0–Z1, with occasional tempo and interval sessions.',
      },
      {
        name: 'Trail',
        description:
          'Off-road running on trails and varied terrain. Builds strength, balance, and mental engagement with elevation changes.',
      },
    ],
  },
  {
    sport: 'Bike',
    intro:
      'Low-impact cross-training for aerobic volume. Useful for building hours without the impact stress of running, especially during high-volume periods.',
    subTypes: [
      {
        name: 'Road Bike',
        description: 'Pavement cycling for steady aerobic volume and longer endurance rides.',
      },
      {
        name: 'Mountain Bike (MTB)',
        description: 'Off-road cycling. Adds variety and works balance, bike handling, and varied terrain efforts.',
      },
    ],
  },
  {
    sport: 'Gym',
    intro:
      'Strength and conditioning work. Supports power development, injury prevention, and ski-specific muscle groups. Tracked by duration only — distance is not applicable.',
    subTypes: [
      {
        name: 'Weights',
        description: 'General and sport-specific strength training in the gym.',
      },
      {
        name: 'Bodyweight',
        description: 'Bodyweight exercises, core work, and calisthenics without external weights.',
      },
    ],
  },
  {
    sport: 'Swim',
    intro:
      'Low-impact full-body cross-training. Develops upper body endurance and aerobic capacity with zero joint stress.',
    subTypes: [
      {
        name: 'Pool',
        description: 'Lap swimming in a pool. Controlled environment for structured intervals and technique work.',
      },
      {
        name: 'Open Water',
        description: 'Swimming in lakes, rivers, or ocean. Adds navigation, sighting, and variable conditions.',
      },
    ],
  },
];

export const SPORT_SECTIONS: SportSection[] = SPORT_SECTIONS_CONTENT.map((section, sportIndex) => ({
  sport: section.sport,
  sportColor: getSportLayerColor(section.sport, sportIndex),
  intro: section.intro,
  subTypes: section.subTypes.map((subType, typeIndex) => ({
    name: subType.name,
    description: subType.description,
    color: getSportTypeColor(section.sport, sportIndex, typeIndex, section.subTypes.length),
  })),
}));

const FOCUS_DESCRIPTIONS: Record<string, string> = {
  Distance:
    'Steady volume-building sessions. The majority of training hours, focused on aerobic base at low-to-moderate intensity.',
  Overdistance:
    'Extra-long sessions beyond typical duration. Builds endurance capacity and mental toughness for race distances.',
  Intensity: 'Structured interval sessions in Z3–Z5. Targets lactate threshold and VO2 max improvements.',
  Speed: 'Short, fast efforts focused on neuromuscular power, technique at race pace, and anaerobic capacity.',
  Race: 'Competition events. Tracked separately to distinguish race stress from training stress.',
  Pace: 'Race-pace simulation sessions. Practices target speed and pacing strategy in training conditions.',
  Recovery:
    'Deliberate easy sessions for restoration. Low heart rate, short duration, promoting adaptation between hard efforts.',
  Other:
    "Sessions that don't fit neatly into the above categories, such as testing, technique drills, or mixed-focus work.",
};

export const FOCUS_CATEGORIES: FocusCategory[] = FOCUS_CONFIGS.map((config) => ({
  name: config.label,
  color: config.color,
  description: FOCUS_DESCRIPTIONS[config.key],
}));

export const CHART_GUIDE_ITEMS: LabeledText[] = [
  {
    label: 'Season Overview:',
    text: 'The interactive line chart at the top of the Dashboard. Toggle layers on/off to compare weekly volume, cumulative hours, distance, and per-sport breakdowns across the full season. Use the time range buttons to zoom into specific periods.',
  },
  {
    label: 'Period Summary:',
    text: "Expandable cards for each mesocycle showing weekly breakdowns, zone distributions, and sport splits. Click any week to jump directly to that week's detail in the Training Log.",
  },
  {
    label: 'Weekly Volume + Zones:',
    text: 'The bar chart shows weekly training hours with color intensity reflecting load level. Nested below it are the Training Zones by Period stacked bar chart (showing zone distribution per mesocycle) and the Zone Distribution donut (showing season-wide zone percentages).',
  },
  {
    label: 'Sport by Period + Distribution:',
    text: 'Stacked bar chart showing how training time is distributed across the four sport types per period, with a yearlong distribution donut nested below.',
  },
  {
    label: 'Sport Type Breakdown:',
    text: "Four quadrant charts showing each sport's sub-type distribution across periods. The focus data grids below each chart show what percentage of that sport's training time went to each focus category.",
  },
  {
    label: 'Training Focus Breakdown:',
    text: 'Duration and distance donut charts showing overall focus distribution, plus a stacked percentage bar chart showing how focus emphasis shifts across periods.',
  },
];
